"use client";

import { motion } from "framer-motion";

const ITEMS_META = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
      </svg>
    ),
    gradient: "from-violet-500/10 to-violet-500/0",
    iconBg: "bg-violet-500/10 border-violet-500/20 text-violet-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
      </svg>
    ),
    gradient: "from-cyan-500/10 to-cyan-500/0",
    iconBg: "bg-cyan-500/10 border-cyan-500/20 text-cyan-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
    ),
    gradient: "from-emerald-500/10 to-emerald-500/0",
    iconBg: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
      </svg>
    ),
    gradient: "from-amber-500/10 to-amber-500/0",
    iconBg: "bg-amber-500/10 border-amber-500/20 text-amber-400",
  },
];

const BEFORE_AFTER = [
  { before: "Calls go to voicemail after hours", after: "Every call answered instantly, 24/7" },
  { before: "Front desk manually routes each call", after: "AI qualifies, routes, or transfers automatically" },
  { before: "No record of missed conversations", after: "Structured summary delivered to your inbox or CRM" },
];

export default function VoiceAgentWhyUs({ heading, items }) {
  const cards = items || [];

  return (
    <section className="bg-zinc-950 py-20 border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-violet-400 uppercase tracking-widest mb-3">Why Asynq</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Built different, by design
          </h2>
          <p className="text-zinc-400 text-lg max-w-lg mx-auto">
            Purpose-built for the way service businesses actually run.
          </p>
        </div>

        {/* Before / After strip */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl border border-white/[0.07] bg-zinc-900/60 divide-y divide-white/[0.05] mb-8 overflow-hidden"
        >
          {BEFORE_AFTER.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 py-4">
              <span className="text-sm text-zinc-500 line-through decoration-zinc-600">{row.before}</span>
              <svg className="w-4 h-4 text-violet-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
              <span className="text-sm text-zinc-200 font-medium">{row.after}</span>
            </div>
          ))}
        </motion.div>

        {/* 2×2 feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {cards.map((item, i) => {
            const meta = ITEMS_META[i % ITEMS_META.length];
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative rounded-2xl border border-white/[0.07] overflow-hidden group hover:border-white/[0.14] transition-all duration-300"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${meta.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative p-7">
                  <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-5 ${meta.iconBg}`}>
                    {meta.icon}
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2 leading-snug">{item.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
